import { useEffect, useRef } from 'react';

// 键盘快捷键（仅桌面）。输入框聚焦时字母键失效，⌘ 组合键仍可用。
// 支持两键序列，如 'g i'。弹窗打开时只响应以 'modal:' 开头注册的按键（弹窗自己的快捷键）。

/** 返回 false 表示这次不处理，按键保持浏览器默认行为（例如有选中文字时的 ⌘C） */
type Handler = (e: KeyboardEvent) => unknown;

function isTyping(e: KeyboardEvent) {
  const el = e.target as HTMLElement | null;
  if (!el) return false;
  return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable || el.tagName === 'SELECT';
}

function keyOf(e: KeyboardEvent) {
  const parts: string[] = [];
  if (e.metaKey || e.ctrlKey) parts.push('mod');
  if (e.shiftKey && e.key.length > 1) parts.push('shift');
  parts.push(e.key.length === 1 ? e.key.toLowerCase() : e.key);
  return parts.join('+');
}

export function useHotkeys(map: Record<string, Handler>, enabled = true) {
  const ref = useRef(map);
  ref.current = map;
  useEffect(() => {
    if (!enabled) return;
    let prefix = '';
    let prefixAt = 0;
    const onKey = (e: KeyboardEvent) => {
      if (e.isComposing) return;
      const k = keyOf(e);
      const typing = isTyping(e);
      if (typing && !k.startsWith('mod+') && k !== 'Escape') return;
      const modalOpen = !!document.querySelector('.wd-scrim, .wd-sheet-scrim');
      const get = (key: string) => (modalOpen ? ref.current[`modal:${key}`] : ref.current[key]);
      const seq = prefix && Date.now() - prefixAt < 900 ? `${prefix} ${k}` : '';
      const h = (seq && get(seq)) || get(k);
      if (h) {
        prefix = '';
        if (h(e) !== false) e.preventDefault();
        return;
      }
      if (Object.keys(ref.current).some((key) => key.startsWith(`${k} `))) {
        prefix = k;
        prefixAt = Date.now();
      } else {
        prefix = '';
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled]);
}
